import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail } from 'lucide-react';

const footerLinks = {
  company: [
    { to: '/',               label: 'Home' },
    { to: '/ecosystem',      label: 'The Ecosystem' },
    { to: '/why-africa',     label: 'Why Africa' },
    { to: '/climate-impact', label: 'Impact' },
    { to: '/faq',            label: 'FAQ' },
    { to: '/terms',          label: 'Terms & Conditions' },
  ],
  invest: [
    { to: '/opportunity',    label: 'The Opportunity' },
    { to: '/sankofa',        label: 'Sankofa Foundry' },
    { to: '/tokenomics',     label: 'Tokenomics' },
    { href: '/documents/azura-whitepaper.pdf', label: 'White Paper ↗', external: true },
  ],
};

/* ── Social icon SVGs ── */
const LinkedInIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
    <rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/>
  </svg>
);
const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622Zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);
const TelegramIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);
const YouTubeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20.05 12 20.05 12 20.05s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z"/>
    <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="#14406f"/>
  </svg>
);

const socials = [
  { label: 'X',         Icon: XIcon,         href: 'https://x.com/AzuraGlobes' },
  { label: 'Telegram',  Icon: TelegramIcon,  href: 'https://t.me/azuramobility' },
  { label: 'Email',     Icon: () => <Mail size={14} />, href: 'mailto:info@azuramobility.com' },
  { label: 'YouTube',   Icon: YouTubeIcon,   href: 'https://www.youtube.com/@azuraglobes' },
];

const contactEmails = [
  { label: 'General Inquiries',  email: 'info@azuramobility.com' },
  { label: 'Investor Relations', email: 'invest@azuramobility.com' },
  { label: 'Partnerships',       email: 'partners@azuramobility.com' },
];

const Footer: React.FC = () => (
  <footer className="bg-bg-dark text-white">

    <div className="h-1 bg-accent-amber w-full" />

    {/* ── Main body ── */}
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12">

        {/* Brand block */}
        <div className="md:col-span-5">
          {/* Logo */}
          <div>
            <img src="/images/logo-white.png" width="180px"alt="Azura Mobility"/>
           
          </div>

          <p className="font-exo font-boldtext-sm text-white/70 leading-relaxed mb-3 max-w-xs">
            Africa's electric mobility infrastructure, built to last. Fleet operations, Battery-as-a-Service, ride-hailing, and the continent's first indigenous EV OEM, launching in Ibadan, 2026.
          </p>

          <p className="text-xs font-bold uppercase tracking-[0.18em] text-accent-amber/70 mb-6">
            Driving Africa Forward. Electrically.
          </p>

          {/* Social icons */}
          <div className="flex gap-2 mb-8">
            {socials.map(({ label, Icon, href }) => (
              <a key={label} href={href} aria-label={label}
                target={href.startsWith('http') ? '_blank' : undefined}
                rel={href.startsWith('http') ? 'noreferrer' : undefined}
                className="w-9 h-9 rounded-full border border-white/10 bg-white/5 flex items-center justify-center text-white/70 hover:bg-accent-amber hover:text-bg-dark hover:border-accent-amber transition-all duration-200">
                <Icon />
              </a>
            ))}
          </div>

          {/* Contact emails */}
          <div className="p-5 rounded-2xl border border-white/8 bg-white/[0.04] space-y-3">
            {contactEmails.map(({ label, email }) => (
              <div key={email}>
                <p className="text-xs tracking-[0.18em] text-white/70 uppercase mb-0.5">{label}</p>
                <a href={`mailto:${email}`}
                  className="font-figtree font-bold text-sm text-accent-amber hover:text-white transition-colors block">
                  {email}
                </a>
              </div>
            ))}
            <a href="mailto:info@azuramobility.com"
              className="inline-flex items-center gap-2 mt-2 bg-accent-amber text-bg-dark font-figtree font-bold text-xs uppercase tracking-wider px-5 py-2.5 rounded-full hover:bg-white transition-all">
              Contact Us <ArrowRight size={13} />
            </a>
          </div>
        </div>

        {/* Nav columns */}
        <div className="md:col-span-3">
          <h4 className="text-xs tracking-[0.2em] uppercase text-white/70 mb-5">Company</h4>
          <ul className="space-y-3">
            {footerLinks.company.map(link => (
              <li key={link.to}>
                <Link to={link.to}
                  className="font-exo text-sm text-white/55 hover:text-white hover:pl-1 transition-all duration-200">
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="text-xs tracking-[0.2em] uppercase text-white/70 mb-5">Investors</h4>
          <ul className="space-y-3 mb-8">
            {footerLinks.invest.map(link => (
              <li key={link.label}>
                {'href' in link ? (
                  <a href={link.href}
                    target={link.external ? '_blank' : undefined}
                    rel={link.external ? 'noreferrer' : undefined}
                    className="font-exo text-sm text-white/55 hover:text-white hover:pl-1 transition-all duration-200">
                    {link.label}
                  </a>
                ) : (
                  <Link to={(link as any).to}
                    className="font-exo text-sm text-white/55 hover:text-white hover:pl-1 transition-all duration-200">
                    {link.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>

        </div>
      </div>
    </div>

    {/* ── Bottom bar ── */}
    <div className="border-t border-white/[0.07]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex flex-col md:flex-row justify-between items-center gap-3">
        <p className="font-exo text-xs text-white/70">
          © 2026 Azura Mobility Limited. Ibadan, Nigeria. Whitepaper v2.0.
        </p>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent-amber/50">
          Driving Africa Forward. Electrically.
        </p>
      </div>
    </div>

  </footer>
);

export default Footer;
